import { TRAINING } from './config';
import { isEffectiveSet, volumeKg } from './oneRm';
import { num, plural } from './text';
import type { Advice, Exercise, Muscle, WorkoutSet } from './types';

/** Mejor día histórico de un ejercicio: hace falta el reparto, no solo el total. */
export interface BestSession {
  volumeKg: number;
  effectiveSets: number;
}

export interface WorkoutShape {
  /** Ejercicios en el orden en que se hicieron. */
  exerciseOrder: readonly string[];
  sets: readonly WorkoutSet[];
}

/**
 * Redundancia y fatiga local (SPEC 7.3).
 *
 * Dos ejercicios del mismo patrón y mismo músculo principal en la misma sesión
 * no son "más estímulo": el segundo llega con el músculo ya cansado y suele
 * rendir peor, así que suma fatiga sin sumar trabajo útil.
 *
 * El aviso solo salta si el segundo DE VERDAD rinde por debajo, medido de dos
 * formas: no llega al mínimo de su rango, o hace un 15 % menos de volumen que
 * su propio mejor día. Dos ejercicios repetidos que van bien no molestan a nadie.
 *
 * `bestByExercise` es el mejor día histórico de cada ejercicio; sin historial,
 * ese segundo criterio simplemente no se aplica.
 */
export function redundancyAdvice(
  workout: WorkoutShape,
  exercises: ReadonlyMap<string, Exercise>,
  bestByExercise: ReadonlyMap<string, BestSession>,
): Advice[] {
  const out: Advice[] = [];
  const flagged = new Set<string>();

  for (let j = 1; j < workout.exerciseOrder.length; j++) {
    const secondId = workout.exerciseOrder[j]!;
    if (flagged.has(secondId)) continue;

    const second = exercises.get(secondId);
    if (!second) continue;

    const sets = workout.sets.filter((s) => s.exerciseId === secondId && isEffectiveSet(s));
    if (sets.length === 0) continue;

    // ¿Hay antes algún ejercicio que comparta patrón y músculo principal?
    const earlier = workout.exerciseOrder
      .slice(0, j)
      .map((id) => exercises.get(id))
      .find(
        (e): e is Exercise =>
          e != null && e.pattern === second.pattern && sharesPrimary(e.primary, second.primary),
      );
    if (!earlier) continue;

    const reason = underperformed(second, sets, bestByExercise.get(secondId));
    if (!reason) continue;

    flagged.add(secondId);
    out.push({
      type: 'redundancia',
      priority: 3,
      message: `${second.name} repite el trabajo de ${earlier.name} y está rindiendo por debajo. Puedes quitarlo, moverlo después de otro grupo muscular o sustituirlo por otro patrón.`,
      reason,
    });
  }

  return out;
}

function sharesPrimary(a: readonly Muscle[], b: readonly Muscle[]): boolean {
  return a.some((muscle) => b.includes(muscle));
}

/** Devuelve el motivo concreto, o null si el ejercicio va bien. */
function underperformed(
  exercise: Exercise,
  sets: readonly WorkoutSet[],
  best: BestSession | undefined,
): string | null {
  const short = sets.filter((s) => s.reps < exercise.repRange.min);
  if (short.length > 0) {
    const worst = Math.min(...short.map((s) => s.reps));
    return `${plural(short.length, 'serie', 'series')} por debajo del mínimo de ${exercise.repRange.min} repeticiones (la peor, ${num(worst)}).`;
  }

  // POR SERIE, no el total de la sesión: a mitad del ejercicio el total de hoy
  // siempre va por debajo del de un día entero, y el aviso saltaba mientras la
  // persona seguía entrenando (R20).
  if (best != null && best.volumeKg > 0 && best.effectiveSets > 0) {
    const bestPerSet = best.volumeKg / best.effectiveSets;
    const todayPerSet = volumeKg(sets) / sets.length;
    const drop = 1 - todayPerSet / bestPerSet;
    if (drop >= TRAINING.fatigueDropRatio) {
      return `Hoy mueves ${num(todayPerSet)} kg por serie frente a los ${num(bestPerSet)} kg por serie de tu mejor día: un ${num(drop * 100)} % por debajo, más del ${num(TRAINING.fatigueDropRatio * 100)} % que marca la regla.`;
    }
  }

  return null;
}
